import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { employeeAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import './OnboardingPage.css';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [completed, setCompleted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Your details
    fullName: user?.name || '',
    email: user?.email || '',
    // Step 2: First role
    company: '',
    jobTitle: '',
    startDate: '',
    endDate: '',
    currentlyWorking: false
  });

  const steps = [
    { label: 'Your details', number: 1 },
    { label: 'First role', number: 2 },
    { label: "What's next", number: 3 }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep0 = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Enter your full name to continue.';
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email to continue.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.company.trim()) {
      newErrors.company = 'Enter a company name to continue.';
    }
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Enter a job title to continue.';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Select a start date to continue.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 0 && validateStep0()) {
      setCurrentStep(1);
    } else if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      // Update profile with name
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';
      
      await employeeAPI.updateProfile({
        first_name: firstName,
        last_name: lastName,
        name: formData.fullName,
        onboarding_complete: true
      });

      // Create the first record request
      await employeeAPI.createRecordRequest({
        employer_name: formData.company,
        job_title: formData.jobTitle,
        start_date: formData.startDate,
        end_date: formData.currentlyWorking ? null : formData.endDate,
        is_current: formData.currentlyWorking,
        employer_email: '' // Will be routed to general HR
      }).catch(() => {
        // Record request might fail if no email, but onboarding still completes
      });

      setCompleted(true);
      toast.success('Your VETT ID is now live!');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateRange = () => {
    if (!formData.startDate) return '-';
    const start = new Date(formData.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (formData.currentlyWorking) return `${start}, current`;
    if (!formData.endDate) return start;
    const end = new Date(formData.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${start}, ${end}`;
  };

  return (
    <div className="onboarding-shell">
      {/* Navigation */}
      <nav className="onboarding-nav">
        <Link className="onboarding-nav-logo" to="/">
          <div className="onboarding-logo-mark">V</div>
          <span>ETT Pro</span>
        </Link>
      </nav>

      <div className="onboarding-container">
        {/* Stepper (hide on completion) */}
        {!completed && (
          <div className="onboarding-stepper" data-testid="onboarding-stepper">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className={`stepper-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              >
                <div className="stepper-line" />
                <div className="stepper-dot">{index < currentStep ? <Check className="w-4 h-4" /> : step.number}</div>
                <div className="stepper-label">{step.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Form Card */}
        <div className="onboarding-card">
          
          {/* Step 0: Your Details */}
          {!completed && currentStep === 0 && (
            <div className="onboarding-panel" data-testid="step-details">
              <div className="onboarding-eyebrow">STEP 1 OF 3</div>
              <h1 className="onboarding-title">Let's create your VETT ID.</h1>
              <p className="onboarding-subtitle">
                This becomes your permanent professional identity. Free, and yours for life.
              </p>

              <div className="onboarding-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="fullName">Full name</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    className={`form-input ${errors.fullName ? 'error' : ''}`}
                    placeholder="e.g. Maya Rodriguez"
                    value={formData.fullName}
                    onChange={handleChange}
                    maxLength={80}
                    data-testid="input-fullname"
                  />
                  {errors.fullName && <div className="field-error">{errors.fullName}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">Work email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    maxLength={120}
                    data-testid="input-email"
                  />
                  {errors.email && <div className="field-error">{errors.email}</div>}
                  <div className="form-hint">
                    We use this to verify your identity and notify you when employers respond.
                  </div>
                </div>

                <div className="onboarding-nav-buttons">
                  <button className="pro-btn pro-btn-ghost" disabled>Back</button>
                  <button 
                    className="pro-btn pro-btn-primary" 
                    onClick={handleNext}
                    data-testid="btn-next-0"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: First Role */}
          {!completed && currentStep === 1 && (
            <div className="onboarding-panel" data-testid="step-first-role">
              <div className="onboarding-eyebrow">STEP 2 OF 3</div>
              <h1 className="onboarding-title">Add your first role.</h1>
              <p className="onboarding-subtitle">
                Tell us about a past or current job. We'll prepare a record request your employer can co-sign.
              </p>

              <div className="onboarding-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="company">Company name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className={`form-input ${errors.company ? 'error' : ''}`}
                    placeholder="e.g. Stripe"
                    value={formData.company}
                    onChange={handleChange}
                    maxLength={80}
                    data-testid="input-company"
                  />
                  {errors.company && <div className="field-error">{errors.company}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="jobTitle">Job title</label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    className={`form-input ${errors.jobTitle ? 'error' : ''}`}
                    placeholder="e.g. Senior Product Manager"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    maxLength={80}
                    data-testid="input-jobtitle"
                  />
                  {errors.jobTitle && <div className="field-error">{errors.jobTitle}</div>}
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label" htmlFor="startDate">Start date</label>
                    <input
                      type="month"
                      id="startDate"
                      name="startDate"
                      className={`form-input ${errors.startDate ? 'error' : ''}`}
                      value={formData.startDate}
                      onChange={handleChange}
                      data-testid="input-start-date"
                    />
                    {errors.startDate && <div className="field-error">{errors.startDate}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="endDate">End date</label>
                    <input
                      type="month"
                      id="endDate"
                      name="endDate"
                      className="form-input"
                      value={formData.endDate}
                      onChange={handleChange}
                      disabled={formData.currentlyWorking}
                      data-testid="input-end-date"
                    />
                  </div>
                </div>

                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    name="currentlyWorking"
                    checked={formData.currentlyWorking}
                    onChange={handleChange}
                    data-testid="checkbox-current"
                  />
                  <span>I currently work here</span>
                </label>

                <div className="onboarding-nav-buttons">
                  <button className="pro-btn pro-btn-ghost" onClick={handleBack}>Back</button>
                  <button 
                    className="pro-btn pro-btn-primary" 
                    onClick={handleNext}
                    data-testid="btn-next-1"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: What's Next */}
          {!completed && currentStep === 2 && (
            <div className="onboarding-panel" data-testid="step-whats-next">
              <div className="onboarding-eyebrow">STEP 3 OF 3</div>
              <h1 className="onboarding-title">Here's what happens next.</h1>
              <p className="onboarding-subtitle">
                VETT records are dual-signed. Nothing gets published until both you and your employer confirm it.
              </p>

              <div className="next-steps-list">
                <div className="next-step-item">
                  <div className="next-step-num">01</div>
                  <div>
                    <div className="next-step-title">We notify your employer</div>
                    <div className="next-step-desc">
                      An invitation goes to your employer's HR contact for <strong>{formData.company}</strong>, 
                      referencing the role you just added.
                    </div>
                  </div>
                </div>
                <div className="next-step-item">
                  <div className="next-step-num">02</div>
                  <div>
                    <div className="next-step-title">They'll be asked to co-sign</div>
                    <div className="next-step-desc">
                      HR and your manager complete a structured assessment, then confirm the dates, 
                      title, and performance rating are accurate.
                    </div>
                  </div>
                </div>
                <div className="next-step-item">
                  <div className="next-step-num">03</div>
                  <div>
                    <div className="next-step-title">You review before it locks</div>
                    <div className="next-step-desc">
                      You'll see every field before it's published. One click to acknowledge, 
                      then the record becomes a permanent part of your VETT ID.
                    </div>
                  </div>
                </div>
              </div>

              <div className="onboarding-summary">
                <div className="onboarding-summary-label">READY TO SEND</div>
                <div className="onboarding-summary-row">
                  <span>Name</span>
                  <span data-testid="summary-name">{formData.fullName}</span>
                </div>
                <div className="onboarding-summary-row">
                  <span>First role</span>
                  <span data-testid="summary-role">{formData.jobTitle} at {formData.company}</span>
                </div>
                <div className="onboarding-summary-row">
                  <span>Dates</span>
                  <span data-testid="summary-dates">{formatDateRange()}</span>
                </div>
              </div>

              <div className="onboarding-nav-buttons">
                <button className="pro-btn pro-btn-ghost" onClick={handleBack}>Back</button>
                <button 
                  className="pro-btn pro-btn-primary" 
                  onClick={handleFinish}
                  disabled={submitting}
                  data-testid="btn-finish"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>Create my VETT ID <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Completion State */}
          {completed && (
            <div className="onboarding-panel onboarding-done" data-testid="step-complete">
              <div className="onboarding-done-icon">
                <Check className="w-8 h-8" />
              </div>
              <h1 className="onboarding-title">Your VETT ID is live.</h1>
              <p className="onboarding-subtitle">
                Your record request has been sent. We'll email you the moment your employer responds.
              </p>
              <Link 
                to="/professional/dashboard" 
                className="pro-btn pro-btn-primary"
                style={{ marginTop: '24px' }}
                data-testid="btn-go-dashboard"
              >
                Go to your dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
